import { ObservableWrapperService } from 'idk/services/observable-wrapper.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DataRepository } from 'idk/data/data-repository';
import { ApiResult } from 'idk/data/api-result';
import { IUserProfile } from 'idk/models/security/user-profile';
import { Router } from '@angular/router';
import { UserProfileService } from './user-profile.service';
import { SubscriptionHandler } from 'idk/data/subscription-handler';
import { CommonDialogsService } from 'idk/widgets/common-dialogs/common-dialogs.service';
import { IResetPassword } from 'idk/models/security/reset-password';

@Injectable({
    providedIn: 'root',
})
export class AuthenticatorService extends DataRepository {
    public api: string = 'api/security/authentication';

    public get redirectTo(): string {
        return localStorage.getItem('___redirectTo');
    }
    public set redirectTo(value: string) {
        localStorage.setItem('___redirectTo', value);
    }

    private _tokenChecker: any = null;

    public constructor(
        private _router: Router,
        private _dialogs: CommonDialogsService,
        protected http: HttpClient,
        protected _userProfileService: UserProfileService,
        ow: ObservableWrapperService
    ) {
        super(ow);
    }

    public updatePassword(
        context: IResetPassword,
        handler?: SubscriptionHandler<boolean>
    ): Observable<ApiResult<boolean>> {
        const suscription = this.http
            .post<ApiResult<boolean>>(
                this.buildApiCall('Password/Change'),
                context,
                this.buildOptions()
            )
            .pipe(tap((r) => this.process(r)));
        if (handler) {
            suscription.subscribe(this.ow.for(handler));
            return null;
        }
        return suscription;
    }

    public forgotPassword(email: string): Observable<any> {
        return this.http.post('api/auth/forgot-password', email);
    }

    public resetPassword(password: string): Observable<any> {
        return this.http.post('api/auth/reset-password', password);
    }

    public startTokenCheckerProcess(): void {
        if (
            this._userProfileService.user != null &&
            this._tokenChecker == null
        ) {
            const nextCheck =
                this._userProfileService.user.expires.getTime() -
                new Date().getTime() -
                30000;

            const callback: () => void = () => {
                this._tokenChecker = setTimeout(() => {
                    this.refreshUserToken(callback);
                }, nextCheck);
            };

            callback();
        }
    }

    public refreshUserToken(callback: () => void = null): void {
        const roleSelected: boolean = this._userProfileService.role != null;

        this.http
            .post<ApiResult<IUserProfile>>(
                this.buildApiCall(
                    roleSelected ? `Token/Refresh/ForRole/${this.user.sequence}` : 'Token/Refresh'
                ),
                roleSelected ? `"${this._userProfileService.role}"` : null,
                this.buildOptions()
            )
            .pipe(tap((r: ApiResult<IUserProfile>) => this.process(r)))
            .subscribe({
                next: (r: ApiResult<IUserProfile>) => {
                    if (r.valid) {
                        this._userProfileService.user = r.data;
                        this.startTokenCheckerProcess();
                        if (callback) {
                            callback();
                        }
                    } else {
                        this._userProfileService.user = null;
                    }
                },
                error: (r) => {
                    this._userProfileService.user = null;
                    this._dialogs.error(
                        'Lo sentimos, su sesión ha sido finalizada por inactividad, por favor ingrese nuevamente',
                        () => this._router.navigate(['/sign-in'])
                    );
                },
            });
    }

    public signIn(
        credentials: {
            userName: string;
            password: string;
        },
        callback: (response: ApiResult<IUserProfile> | string) => void = null
    ): Promise<boolean> {
        this._userProfileService.role = null;

        return new Promise<boolean>((resolve) => {
            this.http
                .post<ApiResult<IUserProfile>>(
                    this.buildApiCall('SignIn'),
                    credentials,
                    this.buildOptions()
                )
                .pipe(tap((r: ApiResult<IUserProfile>) => this.process(r)))
                .subscribe({
                    next: (r: ApiResult<IUserProfile>) => {
                        if (r.valid) {
                            if (r.data.roles.length == 1) {
                                if (!r.data.canBeImpersonated) {
                                    this._userProfileService.role =
                                        r.data.roles[0];
                                }
                            }

                            this._userProfileService.user = r.data;
                            this.startTokenCheckerProcess();
                            resolve(true);
                        } else {
                            if (callback) {
                                callback(r);
                            }
                            resolve(false);
                        }
                    },
                    error: (r) => {
                        if (callback) {
                            callback(r.message);
                        }
                        resolve(false);
                    },
                });
        });
    }

    public signOut(
        handler?: SubscriptionHandler<boolean>
    ): Observable<boolean> {
        const subscription = this.http
            .get<ApiResult<boolean>>(
                this.buildApiCall('SignOut'),
                this.buildOptions()
            )
            .pipe(
                tap((r: ApiResult<boolean>) => this.process(r)),
                map((r: ApiResult<boolean>) => {
                    this._userProfileService.role = null;
                    this._userProfileService.user = null;
                    if (this._tokenChecker != null) {
                        clearTimeout(this._tokenChecker);
                        this._tokenChecker = null;
                    }
                    return r;
                })
            );

        if (handler) {
            subscription.subscribe(this.ow.for(handler));
            return;
        }

        return subscription.pipe(map((r) => r.data));
    }

    public check(): Observable<boolean> {
        return this._userProfileService.user$.pipe(
            map((up: IUserProfile) => up != null && up.expires >= new Date())
        );
    }

    public get user(): IUserProfile {
        return this._userProfileService.user;
    }

    /*
    public signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this.http.post('api/auth/sign-up', user);
    }

    public unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this.http.post('api/auth/unlock-session', credentials);
    }
    */
}
